import { FairCandidateItemDto } from '@/api/generated';
import ButtonWithLoading from '@/components/ButtonWithLoading.comp.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useUpdateFairCandidate } from '@/views/admin/fairs/useFairsApi.util.ts';
import React, { useId, useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';

const MAX_NOTE_LENGTH = 500;

interface Props {
  adminUserId: string;
  /** The candidate being edited; the modal is hidden without one */
  candidate: FairCandidateItemDto | undefined;
  fairNames: string[];
  onHide: () => void;
  onSaved: () => void;
}

export const EditFairCandidateModal: React.FC<Props> = ({
  adminUserId,
  candidate,
  fairNames,
  onHide,
  onSaved,
}) => {
  const { t } = useTranslationWithPrefix('views.admin.fairs.edit');
  const { isUpdating, updateFairCandidate } = useUpdateFairCandidate(adminUserId);
  const fairListId = useId();
  const [email, setEmail] = useState('');
  const [fair, setFair] = useState('');
  const [failed, setFailed] = useState(false);

  const handleEnter = () => {
    setEmail(candidate?.email ?? '');
    setFair(candidate?.note ?? '');
    setFailed(false);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!candidate) {
      return;
    }
    setFailed(false);
    try {
      await updateFairCandidate({
        dto: {
          newEmail: email.trim() !== candidate.email ? email.trim() : undefined,
          note: fair.trim() || undefined,
        },
        email: candidate.email,
      });
      onSaved();
    } catch {
      setFailed(true);
    }
  };

  return (
    <Modal onEnter={handleEnter} onHide={onHide} show={!!candidate}>
      <Modal.Header closeButton>
        <Modal.Title>{t('title')}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={submit}>
        <Modal.Body>
          {failed && <Alert variant="danger">{t('error')}</Alert>}
          <Form.Group className="mb-3" controlId="fair-candidate-email">
            <Form.Label>{t('emailLabel')}</Form.Label>
            <Form.Control
              disabled={isUpdating}
              onChange={event => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </Form.Group>
          <Form.Group controlId="fair-candidate-fair">
            <Form.Label>{t('fairLabel')}</Form.Label>
            <Form.Control
              disabled={isUpdating}
              list={fairListId}
              maxLength={MAX_NOTE_LENGTH}
              onChange={event => setFair(event.target.value)}
              value={fair}
            />
            <datalist id={fairListId}>
              {fairNames.map(name => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-outline-secondary"
            disabled={isUpdating}
            onClick={onHide}
            type="button"
          >
            {t('cancel')}
          </button>
          <ButtonWithLoading className="btn btn-primary" isLoading={isUpdating} type="submit">
            {t('save')}
          </ButtonWithLoading>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
